/**
 * Head slice → ParsedImage. Pure: runs in the worker and in Vitest (node).
 *
 * `HEAD_SLICE = 65 536` is T1's number (`04` §9): both APP1 segments sit
 * inside it on every Sony and P1 file measured. When the second APP1 is NOT
 * complete inside the slice, `needsLargerHead` is set and the caller re-reads
 * 256 KiB and parses again, recording `head_over_64k` (`indexFile.ts`).
 *
 * DI-D6 (revised) c1: the browser extracts, hashes, previews and groups —
 * nothing here is an algorithm of ours. `normalized` types values and applies
 * the sign of a ref; it corrects nothing (DI-D20: the time is read as
 * written and stays raw; the zone tags are recorded, and applied to the log
 * check's gate time only — `runner/gateTime.ts`, DI-3-S70).
 */

import type {
  ExifValue,
  FileMeta,
  IssueCode,
  IssueSeverity,
  Normalized,
  ParsedImage,
  Rational,
  Triple,
  ValidityIssue,
} from "./types.js";
import {
  NotAJpegError,
  hasEoiNearEnd,
  isJpeg,
  walkHead,
  type JpegHead,
} from "./jpeg/segments.js";
import { readExif } from "./jpeg/exif.js";
import { parseXmpProperties, readPacket } from "./jpeg/xmp.js";
import { FAMILIES, detectFamily } from "./families.js";

export const HEAD_SLICE = 65_536;
export const LARGE_HEAD_SLICE = 262_144;

/** Two positions closer than this (degrees) are the same position (DI-2-1 §2.3). */
export const GPS_AGREE_DEG = 1e-6;

const SEVERITY: Readonly<Record<IssueCode, IssueSeverity>> = {
  not_a_jpeg: "fatal",
  scan_not_in_slice: "warn", // fatal for the head hash only; phase 2 reads the whole file
  no_dimensions: "warn",
  no_exif: "fatal",
  no_xmp: "fatal", // fatal on the Sony / P1 path (`01` §A.2 "mandatory"); warn elsewhere
  no_datetime: "fatal",
  truncated_file: "warn",
  gps_disagrees: "warn",
  unsupported_camera_family: "warn",
  head_over_64k: "info",
  preview_unavailable: "warn",
  hash_failed: "warn",
};

export function issue(
  code: IssueCode,
  detail?: string,
  severity: IssueSeverity = SEVERITY[code],
): ValidityIssue {
  return detail === undefined ? { code, severity } : { code, severity, detail };
}

export interface ParseOptions {
  /**
   * The last bytes of the file (up to `EOI_WINDOW`), when the head does not
   * reach its end. The caller (`indexFile`) reads them so `truncated_file` can
   * be decided without reading the file. Omitted and the head shorter than the
   * file → the end is not checked.
   */
  tail?: Uint8Array;
}

export interface ParseOutput extends ParsedImage {
  /** An APP1 starts inside the head and ends beyond it — read a larger head. */
  needsLargerHead: boolean;
}

// ---------------------------------------------------------------------------
// value helpers — the only place a raw value becomes a number
// ---------------------------------------------------------------------------

function isRational(v: ExifValue | undefined): v is Rational {
  return (
    Array.isArray(v) &&
    v.length === 2 &&
    typeof v[0] === "number" &&
    typeof v[1] === "number"
  );
}

function ratio(v: ExifValue | undefined): number | undefined {
  if (isRational(v)) return v[1] === 0 ? undefined : v[0] / v[1];
  if (typeof v === "number") return v;
  return undefined;
}

function num(v: ExifValue | undefined): number | undefined {
  if (typeof v === "number") return v;
  if (Array.isArray(v) && typeof v[0] === "number") return v[0];
  return undefined;
}

function str(v: ExifValue | undefined): string | undefined {
  return typeof v === "string" ? v : undefined;
}

/** XMP text → number, only when the whole text is a number ("+0.00", "-28.00"). */
function xnum(s: string | undefined): number | undefined {
  if (s === undefined) return undefined;
  const t = s.trim();
  if (t === "" || !/^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(t))
    return undefined;
  return Number(t);
}

function dms(v: ExifValue | undefined): number | undefined {
  if (!Array.isArray(v) || v.length !== 3) return undefined;
  const parts = (v as Rational[]).map((r) =>
    isRational(r) ? ratio(r) : undefined,
  );
  const [d, m, s] = parts;
  if (d === undefined || m === undefined || s === undefined) return undefined;
  return d + m / 60 + s / 3600;
}

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/** `[1, 1000]` → `"1/1000"`; `[10, 10000]` → `"1/1000"`; `[5, 2]` → `"2.5"`. */
export function renderExposureTime(
  v: ExifValue | undefined,
): string | undefined {
  if (!isRational(v)) return undefined;
  const [n, d] = v;
  if (d === 0) return undefined;
  if (n === 0) return "0";
  const g = gcd(Math.abs(n), Math.abs(d));
  const rn = n / g;
  const rd = d / g;
  if (rd === 1) return String(rn);
  if (rn === 1) return `1/${String(rd)}`;
  if (rn > rd) return String(rn / rd);
  return `${String(rn)}/${String(rd)}`;
}

const EXIF_DATE = /^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/;

/**
 * EXIF `YYYY:MM:DD HH:MM:SS` + a sub-second STRING → epoch ms, read AS UTC.
 * The sub-second is right-padded / truncated to three digits: "932" → 932,
 * "7" → 700, "9321" → 932, none → 0.
 */
export function exifTimeToUtcMs(
  dateTime: string | undefined,
  subSec: string | undefined,
): number | undefined {
  if (dateTime === undefined) return undefined;
  const m = EXIF_DATE.exec(dateTime.trim());
  if (!m) return undefined;
  const digits = (subSec ?? "").trim();
  const ms = /^\d+$/.test(digits)
    ? Number(digits.padEnd(3, "0").slice(0, 3))
    : 0;
  const [, y, mo, d, h, mi, s] = m.map(Number);
  const t = Date.UTC(
    y ?? 0,
    (mo ?? 1) - 1,
    d ?? 1,
    h ?? 0,
    mi ?? 0,
    s ?? 0,
    ms,
  );
  return Number.isNaN(t) ? undefined : t;
}

const XMP_UTC =
  /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.(\d+))?(Z|[+-]\d{2}:?\d{2})?$/;

/** `drone-dji:UTCAtExposure` ("2026-09-14T15:31:26.767298") → epoch ms, truncated to the ms. */
export function utcAtExposureToMs(s: string | undefined): number | undefined {
  if (s === undefined) return undefined;
  const m = XMP_UTC.exec(s.trim());
  if (!m) return undefined;
  const ms = Number((m[7] ?? "").padEnd(3, "0").slice(0, 3));
  let t = Date.UTC(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
    Number(m[6]),
    ms,
  );
  const zone = m[8];
  if (zone && zone !== "Z") {
    const sign = zone.startsWith("-") ? -1 : 1;
    const hh = Number(zone.slice(1, 3));
    const mm = Number(zone.slice(-2));
    t -= sign * (hh * 60 + mm) * 60_000;
  }
  return Number.isNaN(t) ? undefined : t;
}

function triple(
  xmp: Readonly<Record<string, string>>,
  prefix: "Gimbal" | "Flight",
): Triple | undefined {
  const pitch = xnum(xmp[`${prefix}PitchDegree`]);
  const roll = xnum(xmp[`${prefix}RollDegree`]);
  const yaw = xnum(xmp[`${prefix}YawDegree`]);
  if (pitch === undefined || roll === undefined || yaw === undefined)
    return undefined;
  return { pitch, roll, yaw };
}

/** Assigns only defined values (exactOptionalPropertyTypes: absent, never undefined). */
function put<K extends keyof Normalized>(
  o: Normalized,
  k: K,
  v: Normalized[K] | undefined,
): void {
  if (v !== undefined) o[k] = v;
}

export function normalize(
  exif: Readonly<Record<string, ExifValue>>,
  xmp: Readonly<Record<string, string>>,
  sof: JpegHead["sof"],
): Normalized {
  const n: Normalized = {};
  put(
    n,
    "capturedAtMs",
    exifTimeToUtcMs(str(exif.DateTimeOriginal), str(exif.SubSecTimeOriginal)),
  );
  put(n, "offsetTimeOriginal", str(exif.OffsetTimeOriginal));
  put(n, "offsetTime", str(exif.OffsetTime));
  put(n, "utcAtExposureMs", utcAtExposureToMs(xmp.UTCAtExposure));

  const exifLat = dms(exif.GPSLatitude);
  const exifLon = dms(exif.GPSLongitude);
  if (exifLat !== undefined && exifLon !== undefined) {
    n.lat = str(exif.GPSLatitudeRef) === "S" ? -exifLat : exifLat;
    n.lon = str(exif.GPSLongitudeRef) === "W" ? -exifLon : exifLon;
    n.gpsSource = "exif";
  } else {
    const xLat = xnum(xmp.GpsLatitude);
    const xLon = xnum(xmp.GpsLongitude);
    if (xLat !== undefined && xLon !== undefined) {
      n.lat = xLat;
      n.lon = xLon;
      n.gpsSource = "xmp";
    }
  }

  const alt = ratio(exif.GPSAltitude);
  if (alt !== undefined) n.altAbs = num(exif.GPSAltitudeRef) === 1 ? -alt : alt;
  put(n, "altAbsXmp", xnum(xmp.AbsoluteAltitude));
  put(n, "altRel", xnum(xmp.RelativeAltitude));
  put(n, "gimbal", triple(xmp, "Gimbal"));
  put(n, "flight", triple(xmp, "Flight"));
  const sx = xnum(xmp.FlightXSpeed);
  const sy = xnum(xmp.FlightYSpeed);
  const sz = xnum(xmp.FlightZSpeed);
  if (sx !== undefined && sy !== undefined && sz !== undefined)
    n.speeds = { x: sx, y: sy, z: sz };
  put(n, "lrfTargetDistanceM", xnum(xmp.LRFTargetDistance));
  put(n, "exposureTime", renderExposureTime(exif.ExposureTime));
  put(n, "fNumber", ratio(exif.FNumber));
  put(n, "iso", num(exif.ISOSpeedRatings));
  put(n, "focalLengthMm", ratio(exif.FocalLength));
  // SOF first; a 0 in the SOF (or no SOF inside the head, the M4T) falls back to EXIF
  const sofOk = sof !== undefined && sof.width > 0 && sof.height > 0;
  put(n, "width", sofOk ? sof.width : num(exif.ExifImageWidth));
  put(n, "height", sofOk ? sof.height : num(exif.ExifImageHeight));
  put(n, "make", str(exif.Make));
  put(n, "model", str(exif.Model));
  put(n, "software", str(exif.Software));
  return n;
}

// ---------------------------------------------------------------------------

export function parseFile(
  head: Uint8Array,
  file: FileMeta,
  opts: ParseOptions = {},
): ParseOutput {
  const base: ParseOutput = {
    ...file,
    family: "unknown",
    exif: {},
    xmp: {},
    xmpOther: {},
    normalized: {},
    issues: [],
    ok: false,
    scanStart: null,
    headBytes: head.length,
    needsLargerHead: false,
  };

  let j: JpegHead;
  try {
    j = walkHead(head);
  } catch (e) {
    if (e instanceof NotAJpegError || !isJpeg(head)) {
      base.issues.push(issue("not_a_jpeg"));
      return base;
    }
    throw e;
  }

  const issues: ValidityIssue[] = [];
  if (j.exif) {
    const r = readExif(
      head.subarray(j.exif.payloadStart + 6, j.exif.payloadEnd),
    );
    if (r) base.exif = r.tags;
  }
  if (j.xmp) {
    const packet = readPacket(head, j.xmp.payloadStart, j.xmp.payloadEnd);
    const props = parseXmpProperties(packet.text);
    base.xmp = props.droneDji;
    base.xmpOther = props.other;
  }
  base.family = detectFamily(base.exif, base.xmp, base.xmpOther);
  base.normalized = normalize(base.exif, base.xmp, j.sof);
  base.scanStart = j.sosOffset >= 0 ? j.sosOffset : null;
  base.needsLargerHead = j.app1Incomplete;

  const mvp = FAMILIES[base.family].mvp;
  if (j.sosOffset < 0 && !j.sawEoi) issues.push(issue("scan_not_in_slice"));
  if (
    base.normalized.width === undefined ||
    base.normalized.height === undefined
  )
    issues.push(issue("no_dimensions"));
  if (Object.keys(base.exif).length === 0) issues.push(issue("no_exif"));
  if (
    Object.keys(base.xmp).length === 0 &&
    Object.keys(base.xmpOther).length === 0
  )
    issues.push(issue("no_xmp", undefined, mvp ? "fatal" : "warn"));
  if (base.normalized.capturedAtMs === undefined)
    issues.push(issue("no_datetime"));

  // truncated_file: the image must reach its EOI. Decidable when the head IS
  // the whole file (then a segment cut off before SOS is proof too), or when
  // the caller handed us the file's last bytes. See EOI_WINDOW for why this is
  // "an EOI near the end", not "the last two bytes are FF D9".
  const whole = head.length >= file.size;
  const tail = whole ? head.subarray(0, file.size) : opts.tail;
  if (
    tail !== undefined &&
    ((whole && j.segmentIncomplete) || !hasEoiNearEnd(tail))
  )
    issues.push(issue("truncated_file"));

  // gps_disagrees: EXIF and XMP position must agree within 1e-6°.
  const exifLat = dms(base.exif.GPSLatitude);
  const exifLon = dms(base.exif.GPSLongitude);
  const xLat = xnum(base.xmp.GpsLatitude);
  const xLon = xnum(base.xmp.GpsLongitude);
  if (
    exifLat !== undefined &&
    exifLon !== undefined &&
    xLat !== undefined &&
    xLon !== undefined
  ) {
    const lat = str(base.exif.GPSLatitudeRef) === "S" ? -exifLat : exifLat;
    const lon = str(base.exif.GPSLongitudeRef) === "W" ? -exifLon : exifLon;
    const dLat = Math.abs(lat - xLat);
    const dLon = Math.abs(lon - xLon);
    if (dLat > GPS_AGREE_DEG || dLon > GPS_AGREE_DEG)
      issues.push(
        issue(
          "gps_disagrees",
          `EXIF − XMP: Δlat ${dLat.toExponential(2)}°, Δlon ${dLon.toExponential(2)}°`,
        ),
      );
  }

  if (base.family === "unknown") {
    const make = str(base.exif.Make) ?? "?";
    const model = str(base.exif.Model) ?? "?";
    issues.push(issue("unsupported_camera_family", `${make} ${model}`));
  }

  base.issues = issues;
  base.ok = !issues.some((i) => i.severity === "fatal");
  return base;
}
